import CheckIcon from "@mui/icons-material/Check";
import { Box, Button } from "@mui/material";
import { Map } from "ol";
import Polygon from "ol/geom/Polygon";
import React, { useRef } from "react";
import { UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useGenericDraw } from "~/utils/hooks/useGenericDraw";
import { PolygonCoords } from "~/utils/hooks/usePolygonDraw";
import { useVectorLayer } from "../MapPage/functions/useVectorLayer";
import useCreateMap from "../MapPage/hooks/useCreateMap";
import { LocationAndResponsibilityFormProps } from "./AtonSearchFormContainers/LocationAndResponsibilityForm";

type AreaDrawDialogProps = {
  mapPoints: PolygonCoords;
  setMapPoints: (points: PolygonCoords) => void;
  handleClose: () => void;
  setValue: UseFormSetValue<LocationAndResponsibilityFormProps>;
};

export const AreaDrawDialog = ({
  mapPoints,
  setMapPoints,
  handleClose,
  setValue,
}: AreaDrawDialogProps): JSX.Element => {
  const { createMap } = useCreateMap();
  const mapRef = useRef<Map>();
  const [mapState, setMapState] = React.useState<Map | undefined>();
  const { t } = useTranslation("");
  const mapContainerRef = useRef<HTMLDivElement>();
  const { vectorLayer } = useVectorLayer({ map: mapState });

  const { itemCoordinates, clearDrawing } = useGenericDraw({
    drawLayer: vectorLayer,
    map: mapState,
    coordinates: mapPoints,
    GeometryClass: Polygon,
  });

  React.useEffect(() => {
    if (mapContainerRef.current && !mapState) {
      const createdMap = createMap({
        target: mapContainerRef.current,
        existingMap: undefined,
      });
      mapRef.current = createdMap;
      setMapState(createdMap);
    }
  }, [createMap, mapState]);

  React.useEffect(() => {
    if (mapPoints !== itemCoordinates && itemCoordinates.length !== 0)
      setMapPoints(itemCoordinates);
  }, [itemCoordinates, mapPoints, setMapPoints]);

  const handleClear = React.useCallback(() => {
    setMapPoints([]);
    clearDrawing();
    setValue("points", []);
  }, [clearDrawing, setMapPoints, setValue]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", flex: 1 }} ref={mapContainerRef} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          position: "absolute",
          bottom: "10px",
          left: 0,
          right: 0,
          maxWidth: "max-content",
          margin: "auto",
        }}
      >
        <Button
          sx={{ py: "10px", px: "15px" }}
          variant="contained"
          onClick={handleClear}
        >
          {t("general:clear")}
        </Button>
        <Button
          sx={{ py: "10px", px: "15px" }}
          variant="contained"
          color="success"
          onClick={handleClose}
          endIcon={<CheckIcon />}
        >
          {t("general:finish")}
        </Button>
      </Box>
    </Box>
  );
};
